import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import SharePopup from "./SharePopup";
import CookingMode from "./CookingMode";
import { FaShoppingCart } from "react-icons/fa";
import { toast } from "../lib/toast";

function MediaCarousel({ media, alt }) {
  const [hovering, setHovering] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);
  const videoRef = useRef(null);

  const hasMultiple = media?.length > 1;

  const stopCycling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const advanceToNext = useCallback(() => {
    if (!media?.length) return;
    setActiveIndex((prev) => (prev + 1) % media.length);
  }, [media]);

  const startImageCycling = useCallback(() => {
    if (!hasMultiple || intervalRef.current) return;
    // Only start interval if current item is an image
    const current = media?.[activeIndex];
    if (current?.media_type === 'video') return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % media.length;
        // If next item is a video, stop the interval — video will handle its own advance
        if (media[next]?.media_type === 'video') {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return next;
      });
    }, 1500);
  }, [activeIndex, hasMultiple, media]);

  const handleMouseEnter = () => {
    setHovering(true);
    startImageCycling();
  };

  const handleMouseLeave = () => {
    setHovering(false);
    setActiveIndex(0);
    stopCycling();
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    return () => stopCycling();
  }, [stopCycling]);

  // Handle video play/pause and advance when video ends
  useEffect(() => {
    const current = media?.[activeIndex];
    if (current?.media_type === 'video' && videoRef.current) {
      if (hovering) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
    // If current is an image and we're hovering, restart image cycling
    if (current?.media_type !== 'video' && hovering && hasMultiple && !intervalRef.current) {
      startImageCycling();
    }
  }, [activeIndex, hovering, hasMultiple, media, startImageCycling]);

  const current = media?.[activeIndex];

  return (
    <div
      className="relative group/img"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!media?.length ? (
        <div className="w-full h-40 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
          No image
        </div>
      ) : current?.media_type === "video" && current?.video ? (
        <video
          ref={videoRef}
          src={current.video}
          className={`w-full transition-all duration-300 ${hovering ? "h-64 object-contain bg-black" : "h-40 object-cover"}`}
          muted
          playsInline
          onEnded={hasMultiple ? advanceToNext : undefined}
          loop={!hasMultiple}
        />
      ) : (
        <img
          src={current?.image}
          alt={alt}
          className="w-full h-40 object-cover transition-opacity duration-300"
        />
      )}

      {/* Dot indicators */}
      {hasMultiple && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
          {media.map((_, i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition ${
                i === activeIndex ? "bg-white" : "bg-white/40"
              }`}
            />
          ))}
        </div>
      )}

    </div>
  );
}

const RecipeCard = ({ recipe, cartRecipeIds }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isOwner = currentUser?.id === recipe.owner?.id;
  const [showDropdown, setShowDropdown] = useState(false);
  const [collections, setCollections] = useState([]);
  const [savedInIds, setSavedInIds] = useState([]);
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [createCollectionLoading, setCreateCollectionLoading] = useState(false);
  const [visibility, setVisibility] = useState(recipe.visibility);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [showCookingMode, setShowCookingMode] = useState(false);
  const [fullRecipe, setFullRecipe] = useState(null);
  const dropdownRef = useRef(null);

  // Use parent-provided cart IDs if available, otherwise fetch individually
  const checkCart = useCallback(async () => {
    if (cartRecipeIds) return; // parent handles it
    try {
      const res = await axiosInstance.get("/api/cart/");
      const ids = (res.data.items || []).map((item) => item.recipe.id);
      setInCart(ids.includes(recipe.id));
    } catch {
      // No cart yet
    }
  }, [recipe.id, cartRecipeIds]);

  useEffect(() => {
    if (cartRecipeIds) {
      setInCart(cartRecipeIds.includes(recipe.id));
    } else {
      checkCart();
      const handler = () => checkCart();
      window.addEventListener("cart-changed", handler);
      return () => window.removeEventListener("cart-changed", handler);
    }
  }, [cartRecipeIds, recipe.id, checkCart]);

  const handleGetIngredients = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCartLoading(true);
    try {
      if (inCart) {
        await axiosInstance.delete(`/api/cart/recipes/${recipe.id}/remove/`);
        setInCart(false);
      } else {
        await axiosInstance.post(`/api/cart/recipes/${recipe.id}/`);
        setInCart(true);
      }
      window.dispatchEvent(new Event("cart-changed"));
    } catch (err) {
      if (err.response?.status === 409) setInCart(true);
      else console.error("Failed to update cart:", err);
    } finally {
      setCartLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [colRes, statusRes] = await Promise.all([
        axiosInstance.get("/api/bookmarks/collections/"),
        axiosInstance.get(`/api/bookmarks/recipes/${recipe.id}/status/`),
      ]);
      setCollections(colRes.data);
      setSavedInIds(statusRes.data.collection_ids);
    } catch (err) {
      console.error("Failed to load bookmark data", err);
    }
  };

  const handlePlusClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!showDropdown) {
      fetchDropdownData();
      setShowCreateCollection(false);
      setNewCollectionName("");
    }
    setShowDropdown(!showDropdown);
  };

  const handleToggleCollection = async (e, collectionId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (savedInIds.includes(collectionId)) {
        await axiosInstance.delete(`/api/bookmarks/collections/${collectionId}/recipes/${recipe.id}/`);
        setSavedInIds((prev) => prev.filter((id) => id !== collectionId));
      } else {
        await axiosInstance.post(`/api/bookmarks/collections/${collectionId}/recipes/${recipe.id}/`);
        setSavedInIds((prev) => [...prev, collectionId]);
      }
      window.dispatchEvent(new Event("bookmark-changed"));
    } catch (err) {
      console.error("Failed to toggle bookmark", err);
    }
  };

  const handleCreateCollectionAndSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const name = newCollectionName.trim();
    if (!name || createCollectionLoading) return;

    setCreateCollectionLoading(true);
    try {
      const collectionRes = await axiosInstance.post("/api/bookmarks/collections/", { name });
      const collection = collectionRes.data;

      await axiosInstance.post(
        `/api/bookmarks/collections/${collection.id}/recipes/${recipe.id}/`
      );

      setCollections((prev) => [...prev, collection]);
      setSavedInIds((prev) =>
        prev.includes(collection.id) ? prev : [...prev, collection.id]
      );
      setNewCollectionName("");
      setShowCreateCollection(false);
      window.dispatchEvent(new Event("bookmark-changed"));
      toast.success(`Saved to ${collection.name}.`);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Bookmark already exists.");
      } else {
        console.error("Failed to create collection", err);
        toast.error("Failed to create bookmark.");
      }
    } finally {
      setCreateCollectionLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
        setShowCreateCollection(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleVisibilityToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (visibility === "private") {
      setShowSharePopup(true);
    } else {
      try {
        await axiosInstance.patch(`/api/recipes/${recipe.id}/update/`, { visibility: "private" });
        setVisibility("private");
      } catch (err) {
        console.error("Failed to update visibility", err);
      }
    }
  };

  const handleStartCooking = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await axiosInstance.get(`/api/recipes/${recipe.id}/`);
      setFullRecipe(res.data);
      setShowCookingMode(true);
    } catch (err) {
      console.error("Failed to load recipe:", err);
    }
  };

  const handleShareConfirm = async (message) => {
    try {
      const data = new FormData();
      data.append("visibility", "public");
      if (message.trim()) {
        data.append("share_message", message);
      }
      await axiosInstance.patch(`/api/recipes/${recipe.id}/update/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVisibility("public");
      setShowSharePopup(false);
    } catch (err) {
      console.error("Failed to make public", err);
    }
  };

  return (
    <>
      <Link to={`/recipes/${recipe.id}`} className="block cursor-pointer h-full">
        <div className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/50 rounded-lg overflow-hidden hover:shadow-lg transition duration-200 ease-in-out relative h-full flex flex-col">
          <MediaCarousel
            media={[...(recipe.images || [])].sort((a, b) => {
              if (a.media_type !== b.media_type) return a.media_type === 'video' ? -1 : 1;
              return b.id - a.id;
            })}
            alt={recipe.title}
          />
          <div className="p-4 flex flex-col flex-1 relative group/text">
            {/* Cook It! overlay on text area */}
            <div className="pointer-events-none absolute left-0 right-0 top-0 h-24 bg-white/85 dark:bg-gray-800/85 opacity-0 group-hover/text:opacity-100 flex items-start justify-center pt-4 transition-opacity duration-200 z-10">
              <button
                onClick={handleStartCooking}
                className="pointer-events-auto bg-blue-500 text-white font-semibold px-5 py-2 rounded-lg text-sm shadow-lg hover:bg-blue-600 transition"
              >
                Cook It!
              </button>
            </div>
            <h3 className="text-lg font-semibold mb-1 dark:text-white">{recipe.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{recipe.description}</p>
            {recipe.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {recipe.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/recipes?view=discover&tag=${tag.name}`);
                    }}
                    className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/60 transition"
                  >
                    #{tag.name}
                  </span>
                ))}
                {recipe.tags.length > 3 && (
                  <span className="text-xs text-gray-400">+{recipe.tags.length - 3}</span>
                )}
              </div>
            )}
            <div className="mt-auto pt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>Prep: {recipe.prep_time} min</span> •{" "}
              <span>Cook: {recipe.cook_time} min</span>
            </div>
            <div className="flex items-center justify-between mt-1 relative z-20">
              <div className="text-xs text-gray-400 italic capitalize">
                {recipe.difficulty}
              </div>
              <div className="flex items-center space-x-2">
                {/* Visibility toggle — owner only */}
                {isOwner && (
                  <div className="flex items-center space-x-1.5" onClick={(e) => e.preventDefault()}>
                    <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">{visibility}</span>
                    <button
                      onClick={handleVisibilityToggle}
                      className="relative inline-flex items-center w-9 h-5 rounded-full transition-colors bg-gray-300 dark:bg-gray-600"
                      title={`Switch to ${visibility === "public" ? "private" : "public"}`}
                    >
                      <span
                        className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transition-transform ${
                          visibility === "public" ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>
                )}
              {/* Add to Cart / In Cart button */}
                <button
                  onClick={handleGetIngredients}
                  disabled={cartLoading}
                  className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition ${
                    inCart
                      ? "text-orange-500 bg-orange-50 dark:bg-orange-900/30"
                      : "text-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                  }`}
                  title={inCart ? "View cart" : "Add to cart"}
                >
                  <FaShoppingCart size={10} />
                  <span>{cartLoading ? "..." : inCart ? "In Cart" : "Add to Cart"}</span>
                </button>
                {/* Bookmark button */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={handlePlusClick}
                    className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-blue-500 hover:text-white text-gray-600 dark:text-gray-400 flex items-center justify-center text-xs font-bold transition"
                    title="Add to bookmark"
                  >
                    +
                  </button>
                  {showDropdown && (
                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="absolute right-0 bottom-8 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg w-44 z-50 max-h-48 overflow-y-auto">
                      <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase border-b dark:border-gray-700">Save to...</p>
                      <button
                        type="button"
                        onClick={() => setShowCreateCollection((prev) => !prev)}
                        className="block w-full text-left px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700"
                      >
                        + New bookmark
                      </button>
                      {showCreateCollection && (
                        <div className="border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                          <input
                            type="text"
                            value={newCollectionName}
                            onChange={(e) => setNewCollectionName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleCreateCollectionAndSave(e);
                              }
                            }}
                            placeholder="Bookmark name..."
                            className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={handleCreateCollectionAndSave}
                            disabled={!newCollectionName.trim() || createCollectionLoading}
                            className="mt-2 w-full rounded-md bg-blue-500 px-2 py-1.5 text-xs font-medium text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {createCollectionLoading ? "Saving..." : "Create and save"}
                          </button>
                        </div>
                      )}
                      {collections.length === 0 ? (
                        <p className="px-3 py-3 text-xs text-gray-400 text-center">No bookmarks yet</p>
                      ) : (
                        collections.map((col) => (
                          <button
                            key={col.id}
                            onClick={(e) => handleToggleCollection(e, col.id)}
                            className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${savedInIds.includes(col.id) ? "text-blue-600" : "text-gray-700 dark:text-gray-300"}`}
                          >
                            {col.name} {savedInIds.includes(col.id) && "✓"}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Share popup when toggling private → public */}
      {showSharePopup && (
        <SharePopup
          onConfirm={handleShareConfirm}
          onCancel={() => setShowSharePopup(false)}
        />
      )}

      {/* Cooking mode */}
      {showCookingMode && fullRecipe && (
        <CookingMode recipe={fullRecipe} onClose={() => setShowCookingMode(false)} />
      )}
    </>
  );
};

export default RecipeCard;
